import React, { useEffect, useMemo, useRef, useState } from 'react';
import CloudinaryImageBrowser from '../Cloudinary/CloudinaryImageBrowser';
import RichTextEditor from './RichTextEditor';
import HomeAboutSection from '../Home/HomeAboutSection';
import { DEFAULT_WEBSITE_CONTENT } from '../../config/defaults';
import {
  AboutPortrait,
  FocalPoint,
  HomepageAboutDraft,
  createHomepageAboutDraft,
  validateHomepageAbout,
} from '../../utils/homepageAbout';
import {
  fetchPublishedWebsiteContent,
  mergeWebsiteContent,
  readLocalWebsiteContent,
  savePublishedWebsiteContent,
} from '../../utils/websiteContent';
import './HomepageAboutEditor.css';

interface CloudinaryPick {
  publicId?: string;
  public_id?: string;
  secure_url?: string;
  url?: string;
  width?: number;
  height?: number;
  alt?: string;
  filename?: string;
}

const HomepageAboutEditor: React.FC = () => {
  const [draft, setDraft] = useState<HomepageAboutDraft>(() => createHomepageAboutDraft(DEFAULT_WEBSITE_CONTENT));
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [sourceContent, setSourceContent] = useState(DEFAULT_WEBSITE_CONTENT);
  const [isDirty, setIsDirty] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hasSavedAbout, setHasSavedAbout] = useState(false);
  const dirtyRef = useRef(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [previewLayout, setPreviewLayout] = useState<'published' | 'image' | 'text'>('published');
  const [previewScale, setPreviewScale] = useState(1);
  const [previewHeight, setPreviewHeight] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadingRef = useRef(false);

  const applyDraft = (content: ReturnType<typeof mergeWebsiteContent> | typeof DEFAULT_WEBSITE_CONTENT, savedAbout: boolean) => {
    const next = createHomepageAboutDraft(content);
    dirtyRef.current = false;
    setHasSavedAbout(savedAbout);
    setSourceContent(content);
    setDraft(next);
    setSavedSnapshot(JSON.stringify(next));
    setIsDirty(false);
  };

  const loadDraft = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      let content = readLocalWebsiteContent() || { ...DEFAULT_WEBSITE_CONTENT };
      const remote = await fetchPublishedWebsiteContent();
      if (dirtyRef.current) return;
      if (remote) {
        content = remote;
      } else if (content.homepageAbout) {
        const published = await savePublishedWebsiteContent(mergeWebsiteContent(content));
        if (!published && !dirtyRef.current) {
          setSaveError('About Me is still only in this browser. Save again to publish it on the homepage.');
        }
      }
      if (dirtyRef.current) return;
      applyDraft(content, Boolean(content.homepageAbout));
    } finally {
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    loadDraft();
    const refreshIfClean = () => {
      if (!dirtyRef.current) loadDraft();
    };
    window.addEventListener('website_content_updated', refreshIfClean);
    window.addEventListener('storage', refreshIfClean);
    return () => {
      window.removeEventListener('website_content_updated', refreshIfClean);
      window.removeEventListener('storage', refreshIfClean);
    };
  }, []);

  const update = (patch: Partial<HomepageAboutDraft>) => {
    dirtyRef.current = true;
    setDraft((current) => ({ ...current, ...patch }));
    setIsDirty(true);
    setSaveMessage('');
    setSaveError('');
  };

  const validation = useMemo(() => validateHomepageAbout(draft), [draft]);
  const previewDraft = useMemo(() => {
    if (previewLayout === 'text') return { ...draft, showPortrait: false };
    if (previewLayout === 'image') return { ...draft, showPortrait: true };
    return draft;
  }, [draft, previewLayout]);

  useEffect(() => {
    if (previewDevice !== 'desktop') {
      setPreviewScale(1);
      setPreviewHeight(0);
      return undefined;
    }
    const viewport = viewportRef.current;
    const canvas = canvasRef.current;
    if (!viewport || !canvas) return undefined;
    const updateScale = () => {
      const available = viewport.clientWidth;
      const scale = Math.min(1, available / 1040);
      setPreviewScale(scale);
      setPreviewHeight(canvas.offsetHeight * scale);
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(viewport);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [previewDevice, previewDraft, draft]);

  const applyPortrait = (portrait: AboutPortrait) => {
    update({
      portrait: {
        ...portrait,
        altText: draft.portrait?.altText || portrait.altText || 'Carmen Lorraine',
      },
      showPortrait: true,
    });
    setConfirmRemove(false);
    setPickerOpen(false);
    setUploadError('');
  };

  const portraitFromPick = (image: CloudinaryPick): AboutPortrait | null => {
    const imageUrl = image.secure_url || image.url || '';
    if (!imageUrl) return null;
    return {
      imageUrl,
      publicId: image.publicId || image.public_id || '',
      altText: image.alt || image.filename || 'Carmen Lorraine',
      width: image.width,
      height: image.height,
    };
  };

  const uploadPortrait = async (file: File) => {
    if (!file.type.startsWith('image/') || file.size > 10 * 1024 * 1024) {
      setUploadError('Choose an image under 10MB.');
      return;
    }
    setUploadError('');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'unsigned_upload');
    formData.append('folder', 'carmen_lorraine_photos/homepage');
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'di1mzmlts';
    try {
      const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error('Upload failed');
      const result = await response.json();
      const portrait = portraitFromPick(result);
      if (!portrait) throw new Error('Upload did not return an image');
      applyPortrait(portrait);
    } catch (error) {
      console.error('Error uploading homepage portrait:', error);
      setUploadError('The photograph could not be uploaded. Nothing on the homepage was changed.');
    }
  };

  const handleSave = async () => {
    const result = validateHomepageAbout(draft);
    if (result.errors.length > 0) {
      setSaveError(result.errors[0]);
      setSaveMessage('');
      return;
    }
    setSaving(true);
    try {
      const existing = readLocalWebsiteContent() || { ...DEFAULT_WEBSITE_CONTENT };
      const remote = await fetchPublishedWebsiteContent();
      const next = mergeWebsiteContent({
        ...existing,
        ...(remote || {}),
        homepageAbout: { ...draft },
      });
      const published = await savePublishedWebsiteContent(next);
      if (!published) {
        setSaveMessage('');
        setSaveError('About Me could not be published. The homepage still shows the previous version.');
        return;
      }
      dirtyRef.current = false;
      setHasSavedAbout(true);
      setSavedSnapshot(JSON.stringify(draft));
      setIsDirty(false);
      setSaveError('');
      setSaveMessage('About Me saved. The homepage now shows this version.');
    } catch (error) {
      console.error('Error saving homepage About Me:', error);
      setSaveMessage('');
      setSaveError('About Me could not be saved. The previous homepage content is unchanged.');
    } finally {
      setSaving(false);
    }
  };

  const setFocal = (key: 'desktopPosition' | 'mobilePosition', point: FocalPoint) => {
    update({ [key]: point });
  };

  const unsaved = isDirty || JSON.stringify(draft) !== savedSnapshot;

  return (
    <div className="homepage-about-editor">
      <div className="about-editor-card">
        <h3>Visibility</h3>
        <p>The portrait is optional. Turning it off keeps the selected photograph so you can show it again later.</p>
        <Switch
          pressed={draft.showPortrait}
          onClick={() => update({ showPortrait: !draft.showPortrait })}
          onLabel="Portrait shown on the homepage"
          offLabel="Portrait hidden. Text-only layout"
        />
        {validation.warnings.map((warning) => (
          <p className="about-editor-warning" key={warning}>{warning}</p>
        ))}
      </div>

      <div className="about-editor-card">
        <h3>Portrait</h3>
        <p>Choose a photograph of Carmen, upload a new one, or remove the homepage selection. Removing it does not delete the file from the image library.</p>
        {draft.portrait ? (
          <div className="about-portrait-preview">
            <img src={draft.portrait.imageUrl} alt={draft.portrait.altText || 'Selected portrait'} />
            <div>
              <label htmlFor="portrait-alt">Image description</label>
              <input
                id="portrait-alt"
                className="form-input"
                value={draft.portrait.altText}
                onChange={(event) => update({
                  portrait: { ...draft.portrait!, altText: event.target.value },
                })}
              />
              <p className="about-field-help">Used as the description for people who cannot see the photograph.</p>
            </div>
          </div>
        ) : (
          <p className="about-field-help">No portrait selected. The homepage uses the text-only layout.</p>
        )}
        <div className="about-portrait-actions">
          <button type="button" className="btn btn-secondary" onClick={() => setPickerOpen(true)}>
            {draft.portrait ? 'Replace image' : 'Select image'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
            Upload image
          </button>
          {draft.portrait && (
            <button type="button" className="btn btn-secondary" onClick={() => setConfirmRemove(true)}>
              Remove image
            </button>
          )}
        </div>
        <input
          ref={fileInputRef}
          className="about-upload-input"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) uploadPortrait(file);
          }}
        />
        {uploadError && <p className="about-editor-status is-error">{uploadError}</p>}
        {confirmRemove && (
          <div className="about-remove-confirm">
            <p>Remove this photograph from the homepage? The file stays in your image library.</p>
            <div className="about-portrait-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  update({ portrait: null, showPortrait: false });
                  setConfirmRemove(false);
                }}
              >
                Remove from homepage
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmRemove(false)}>
                Keep image
              </button>
            </div>
          </div>
        )}
        {draft.portrait && (
          <div className="about-focal-grid" style={{ marginTop: '1.25rem' }}>
            <FocalControl
              label="Desktop position"
              value={draft.desktopPosition}
              imageUrl={draft.portrait.imageUrl}
              aspect={draft.portrait.width && draft.portrait.height ? `${draft.portrait.width} / ${draft.portrait.height}` : '3 / 4'}
              onChange={(point) => setFocal('desktopPosition', point)}
            />
            <FocalControl
              label="Mobile position"
              value={draft.mobilePosition}
              imageUrl={draft.portrait.imageUrl}
              aspect={draft.portrait.width && draft.portrait.height ? `${draft.portrait.width} / ${draft.portrait.height}` : '3 / 4'}
              onChange={(point) => setFocal('mobilePosition', point)}
            />
          </div>
        )}
      </div>

      <div className="about-editor-card">
        <h3>Headings</h3>
        <div className="form-group">
          <label htmlFor="about-eyebrow">Small introductory heading</label>
          <input
            id="about-eyebrow"
            className="form-input"
            value={draft.eyebrow}
            onChange={(event) => update({ eyebrow: event.target.value })}
          />
        </div>
        <div className="form-group">
          <label htmlFor="about-heading">Main heading</label>
          <input
            id="about-heading"
            className="form-input"
            value={draft.heading}
            onChange={(event) => update({ heading: event.target.value })}
          />
        </div>
      </div>

      <div className="about-editor-card">
        <h3>Introduction</h3>
        <p>These paragraphs are for the homepage. They begin as the introduction already on the site. After you save, the About page keeps its own copy and the two no longer change together.</p>
        <RichTextEditor
          value={draft.introduction}
          onChange={(introduction) => update({ introduction })}
          rows={8}
          placeholder="Write a short personal introduction"
        />
      </div>

      <div className="about-editor-card">
        <h3>Preview</h3>
        <p>This is the homepage section with your current edits. It changes on the website only after you save.</p>
        <div className="about-preview-toolbar">
          <label>
            Screen
            <select value={previewDevice} onChange={(event) => setPreviewDevice(event.target.value as 'desktop' | 'mobile')}>
              <option value="desktop">Desktop</option>
              <option value="mobile">Mobile</option>
            </select>
          </label>
          <label>
            Layout
            <select value={previewLayout} onChange={(event) => setPreviewLayout(event.target.value as 'published' | 'image' | 'text')}>
              <option value="published">As it will publish</option>
              <option value="image" disabled={!draft.portrait}>With portrait</option>
              <option value="text">Text only</option>
            </select>
          </label>
        </div>
        {previewLayout === 'image' && !draft.portrait && (
          <p className="about-editor-warning">Select a portrait to preview the image layout.</p>
        )}
        <div className="about-preview-viewport" ref={viewportRef}>
          {previewDevice === 'desktop' ? (
            <div className="about-preview-scale-slot" style={{ height: previewHeight || undefined }}>
              <div
                ref={canvasRef}
                className="about-preview-canvas is-desktop"
                style={{ width: 1040, transform: `scale(${previewScale})`, transformOrigin: 'top left' }}
              >
                <HomeAboutSection content={sourceContent} draft={previewDraft} preview="desktop" />
              </div>
            </div>
          ) : (
            <div className="about-preview-canvas is-mobile">
              <HomeAboutSection content={sourceContent} draft={previewDraft} preview="mobile" />
            </div>
          )}
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving || (hasSavedAbout && !unsaved) || validation.errors.length > 0}>
          {saving ? 'Saving…' : 'Save About Me'}
        </button>
        {saveMessage && <p className="about-editor-status is-success" role="status">{saveMessage}</p>}
        {saveError && <p className="about-editor-status is-error" role="alert">{saveError}</p>}
        {!saveError && validation.errors[0] && <p className="about-editor-status is-error" role="alert">{validation.errors[0]}</p>}
      </div>

      {pickerOpen && (
        <CloudinaryImageBrowser
          maxSelection={1}
          onClose={() => setPickerOpen(false)}
          onImagesSelected={(images) => {
            const portrait = images[0] ? portraitFromPick(images[0]) : null;
            if (portrait) applyPortrait(portrait);
            else setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
};

const Switch: React.FC<{
  pressed: boolean;
  onClick: () => void;
  onLabel: string;
  offLabel: string;
}> = ({ pressed, onClick, onLabel, offLabel }) => (
  <button type="button" className={`about-switch${pressed ? ' is-on' : ''}`} aria-pressed={pressed} onClick={onClick}>
    <span className="about-switch-track" aria-hidden="true"><span className="about-switch-thumb" /></span>
    <span>{pressed ? onLabel : offLabel}</span>
  </button>
);

const FocalControl: React.FC<{
  label: string;
  value: FocalPoint;
  imageUrl: string;
  aspect: string;
  onChange: (point: FocalPoint) => void;
}> = ({ label, value, imageUrl, aspect, onChange }) => {
  const setFromEvent = (event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, Math.round(((event.clientX - rect.left) / rect.width) * 100)));
    const y = Math.min(100, Math.max(0, Math.round(((event.clientY - rect.top) / rect.height) * 100)));
    onChange({ x, y });
  };
  return (
    <fieldset className="about-focal-control">
      <legend>{label}</legend>
      <button
        type="button"
        className="about-focal-stage"
        style={{ ['--about-aspect' as string]: aspect }}
        onClick={setFromEvent}
        aria-label={`${label}. Click the photograph to choose the focal point.`}
      >
        <img src={imageUrl} alt="" style={{ objectPosition: `${value.x}% ${value.y}%` }} />
        <span className="about-focal-marker" style={{ left: `${value.x}%`, top: `${value.y}%` }} />
      </button>
      <div className="about-focal-sliders">
        <label>
          Horizontal
          <input
            type="range"
            min={0}
            max={100}
            value={value.x}
            onChange={(event) => onChange({ ...value, x: Number(event.target.value) })}
          />
          <span>{value.x}%</span>
        </label>
        <label>
          Vertical
          <input
            type="range"
            min={0}
            max={100}
            value={value.y}
            onChange={(event) => onChange({ ...value, y: Number(event.target.value) })}
          />
          <span>{value.y}%</span>
        </label>
      </div>
    </fieldset>
  );
};

export default HomepageAboutEditor;
